import React from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  useTheme,
  alpha 
} from '@mui/material';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

const SpinVisualization = ({ 
  spinRpm, 
  deliveryType,
  size = 'medium',
  showLabel = true 
}) => {
  const theme = useTheme();
  
  // Size configurations
  const sizeConfig = {
    small: { size: 80, thickness: 4, fontSize: 24 },
    medium: { size: 120, thickness: 6, fontSize: 32 },
    large: { size: 160, thickness: 8, fontSize: 40 }
  };
  
  const { size: circleSize, thickness, fontSize } = sizeConfig[size];
  
  // Determine spin characteristics based on delivery type
  const getSpinCharacteristics = () => {
    if (!spinRpm) {
      return { 
        type: 'unknown', 
        direction: 'unknown',
        color: theme.palette.text.secondary,
        icon: <AllInclusiveIcon />
      };
    }
    
    // Determine spin type based on RPM and delivery type
    let spinType;
    let direction;
    
    if (deliveryType === 'spin') {
      spinType = 'high';
      direction = spinRpm > 2000 ? 'clockwise' : 'anticlockwise'; // Simplified
    } else if (deliveryType === 'slow') {
      spinType = 'medium';
      direction = 'anticlockwise';
    } else {
      spinType = 'low';
      direction = 'clockwise'; // For seam bowling
    }
    
    const color = spinType === 'high' 
      ? theme.palette.error.main 
      : spinType === 'medium' 
        ? theme.palette.warning.main 
        : theme.palette.info.main;
    
    return {
      type: spinType,
      direction,
      color,
      icon: direction === 'clockwise' 
        ? <RotateRightIcon /> 
        : <RotateLeftIcon />
    };
  };
  
  const spinInfo = getSpinCharacteristics();
  const normalizedRpm = spinRpm ? Math.min(spinRpm / 3000, 1) : 0; // Normalize to 3000 RPM max
  
  // Get spin description
  const getSpinDescription = () => {
    if (!spinRpm) return 'Spin not detected';
    
    if (spinRpm < 500) return 'Very Low Spin';
    if (spinRpm < 1000) return 'Low Spin';
    if (spinRpm < 1500) return 'Medium Spin';
    if (spinRpm < 2000) return 'High Spin';
    return 'Very High Spin';
  };
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      p: 3,
      position: 'relative'
    }}>
      {showLabel && (
        <Typography 
          variant="subtitle1" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            textAlign: 'center',
            mb: 2
          }}
        >
          Ball Spin
        </Typography>
      )}
      
      {/* Spin visualization container */}
      <Box sx={{ 
        position: 'relative',
        width: circleSize,
        height: circleSize,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Background circle */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }} />
        
        {/* Spin progress circle */}
        {spinRpm && (
          <CircularProgress
            variant="determinate"
            value={normalizedRpm * 100}
            size={circleSize}
            thickness={thickness}
            sx={{
              color: spinInfo.color,
              position: 'absolute',
              top: 0,
              left: 0,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
          />
        )}
        
        {/* Animated spin arrows */}
        <Box sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          animation: spinRpm ? `spin ${2 / (normalizedRpm || 0.1)}s linear infinite` : 'none',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
          }
        }}>
          {[0, 90, 180, 270].map((rotation, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: circleSize * 0.4,
                height: 2,
                backgroundColor: spinInfo.color,
                transform: `rotate(${rotation}deg) translateX(-${circleSize * 0.2}px)`,
                transformOrigin: 'left center',
                opacity: 0.6,
              }}
            />
          ))}
        </Box>
        
        {/* Center content */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          borderRadius: '50%',
          width: circleSize * 0.6,
          height: circleSize * 0.6,
          boxShadow: theme.shadows[2],
        }}>
          <Box sx={{ 
            color: spinInfo.color,
            fontSize: fontSize * 0.6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {spinInfo.icon}
          </Box>
          
          {spinRpm ? (
            <>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: spinInfo.color,
                  fontSize: fontSize * 0.7,
                  lineHeight: 1
                }}
              >
                {spinRpm.toFixed(0)}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  mt: 0.5
                }}
              >
                RPM
              </Typography>
            </>
          ) : (
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                textAlign: 'center',
                px: 1
              }}
            >
              No Spin Data
            </Typography>
          )}
        </Box>
      </Box>
      
      {/* Spin info below */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 500,
            color: spinInfo.color
          }}
        >
          {getSpinDescription()}
        </Typography>
        
        {spinRpm && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary,
              display: 'block',
              mt: 0.5
            }}
          >
            {spinInfo.direction === 'clockwise' ? 'Off-spin / Leg-spin' : 'Leg-spin / Off-spin'}
          </Typography>
        )}
        
        {/* Additional info based on delivery type */}
        {deliveryType === 'spin' && spinRpm && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.success.main,
              display: 'block',
              mt: 1,
              fontStyle: 'italic'
            }}
          >
            {spinRpm > 1800 ? 'Excellent spin rate' : 
             spinRpm > 1200 ? 'Good spin rate' : 
             'Moderate spin rate'}
          </Typography>
        )}
      </Box>
      
      {/* Legend */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-around', 
        width: '100%',
        mt: 3,
        pt: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Type
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {deliveryType || 'Unknown'}
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Direction
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {spinInfo.direction}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SpinVisualization;